// frontend/src/components/dialogs/BookingConfirmDialog.js
import { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import { LucideSave, BookX, MapPin, AlertTriangle } from 'lucide-react';
import { t } from 'i18next';
import { useDateTimePreferences } from '../../hooks/useDateTimePreferences';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton } from '../themed';
import { calculateBookingETA, formatDurationText } from '../../utils/etaCalculations';
import { formatDateISO, parseLocalDate } from '../../utils/dateTimeFormatter';
import bookingEtaService from '../../services/bookingEtaService';

const BookingConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    bookingDetails,
    isProcessing = false,
    waypoints,
    pickupStopId,
    dropoffStopId,
    referenceData,
    segmentData, // Segment times for baseline ETA (OpenRouteService)
    entityId = null,
    tripId = null,
    operationalDelay = null,
}) => {
    // Get formatting functions from enhanced hook
    const { formatDate, formatTimeString, formatTime } = useDateTimePreferences();
    const { text, cn } = useTheme();
    const [tripEtaData, setTripEtaData] = useState(null);
    const operationalDelayMinutes = Number(operationalDelay?.originDepartureMinutes);
    const hasExactSingleOccurrence = bookingDetails?.bookingMode !== 'all'
        && bookingDetails?.dates?.length === 1;
    const hasOperationalDelay = hasExactSingleOccurrence
        && Number.isInteger(operationalDelayMinutes)
        && operationalDelayMinutes > 0;

    useEffect(() => {
        let cancelled = false;

        const loadTripEtaData = async () => {
            if (!entityId || !tripId) {
                setTripEtaData(null);
                return;
            }

            try {
                const data = await bookingEtaService.getTripEtaData(entityId, tripId);
                if (!cancelled) {
                    setTripEtaData(data);
                }
            } catch (error) {
                console.warn(`Failed to fetch trip-aware booking ETA data for trip ${tripId}:`, error);
                if (!cancelled) {
                    setTripEtaData(null);
                }
            }
        };

        loadTripEtaData();

        return () => {
            cancelled = true;
        };
    }, [entityId, tripId]);
    
    // Calculate ETA information for the booking
    const etaInfo = useMemo(() => {
        console.log('BookingConfirmDialog ETA calculation inputs:', {
            time: bookingDetails?.time,
            timeValue: bookingDetails?.timeValue,
            waypoints: waypoints?.length,
            pickupStopId,
            dropoffStopId,
            referenceData: referenceData,
            segmentData: segmentData?.length,
            dates: bookingDetails?.dates
        });

        // Get the actual time value (might be in timeValue or time field)
        const timeValue = bookingDetails?.timeValue || bookingDetails?.time;

        if (!timeValue || !waypoints || !pickupStopId || !dropoffStopId) {
            console.log('Missing required data for ETA calculation');
            return null;
        }

        // Use the first date from the booking (convert from formatted date to ISO format)
        let tripDate;
        if (bookingDetails.dates?.[0]) {
            // Always parse the date in a locale-agnostic way
            const dateValue = bookingDetails.dates[0];
            const parsedDate = parseLocalDate(dateValue);
            if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
                console.warn('Could not parse booking date, falling back to today', { dateValue });
                tripDate = formatDateISO(new Date());
            } else {
                tripDate = formatDateISO(parsedDate);
            }
        } else {
            tripDate = formatDateISO(new Date());
        }

        const result = calculateBookingETA(
            tripEtaData?.waypoints || waypoints,
            timeValue,
            tripDate,
            pickupStopId,
            dropoffStopId,
            referenceData,
            tripEtaData?.segments || segmentData // Pass segment data for baseline ETA
        );

        console.log('BookingConfirmDialog ETA calculation result:', result);
        return result;
    }, [bookingDetails, tripEtaData, waypoints, pickupStopId, dropoffStopId, referenceData, segmentData]);
    
    if (!bookingDetails) return null;
    console.log('Booking confirmation dialog opened', { 
        routeName: bookingDetails.routeName,
        passengers: bookingDetails.passengers,
        bookingMode: bookingDetails.bookingMode,
        dates: bookingDetails.dates,
        startDate: bookingDetails.startDate,
        endDate: bookingDetails.endDate
    });
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('confirmBooking')}
            size="md"
            footer={
                <div className="flex justify-end gap-4">
                    <ThemedButton
                        onClick={() => {
                            // Pass estimated pickup time to the confirm handler, formatted as HH:MM
                            let estimatedPickupTime = null;
                            if (etaInfo && !etaInfo.summary.isPickupStart && etaInfo.pickupLocation.time) {
                                const pickupTime = new Date(etaInfo.pickupLocation.time);
                                // Format as HH:MM for database TIME field
                                estimatedPickupTime = pickupTime.toTimeString().slice(0, 5);
                            }
                            if (bookingDetails?.confirmPastDeparture) {
                                onConfirm({ confirmPastDeparture: true, estimatedPickupTime });
                            } else {
                                onConfirm(estimatedPickupTime);
                            }
                        }}
                        disabled={isProcessing}
                        variant="success"
                        className="px-4 py-2"
                    >
                        <div className="flex items-center gap-2">
                            <LucideSave className="w-5 h-5" />
                            {t('Confirm')}
                        </div>
                    </ThemedButton>
                    <ThemedButton
                        onClick={onClose}
                        disabled={isProcessing}
                        variant="secondary"
                        className="px-4 py-2"
                    >
                        <div className="flex items-center gap-2">
                            <BookX className="w-5 h-5" />
                            {t('Cancel')}
                        </div>
                    </ThemedButton>
                </div>
            }
        >
            <div className="space-y-4">
                <div>
                    <p className={cn("font-medium", text.primary)}>{t('Route')}</p>
                    <p className={text.secondary}>{bookingDetails.routeName}</p>
                </div>
                <div>
                    <p className={cn("font-medium", text.primary)}>{t('Pickup Location')}</p>
                    <p className={text.secondary}>{bookingDetails.pickup}</p>
                </div><div>
                    <p className={cn("font-medium", text.primary)}>{t('Dropoff Location')}</p>
                    <p className={text.secondary}>{bookingDetails.dropoff}</p>
                </div>
                <div>
                    <p className={cn("font-medium", text.primary)}>{t('Dates')}</p>
                    {bookingDetails.bookingMode === 'all' ? (
                        <p className={text.secondary}>
                            {t('All available days from')} {formatDate(bookingDetails.startDate)} {t('to')} {formatDate(bookingDetails.endDate)}
                        </p>
                    ) : (
                        <ul className={cn("list-disc list-inside", text.secondary)}>
                            {bookingDetails.dates.map(date => {
                                console.log('Formatting date:', date, typeof date);
                                const formattedDate = formatDate(date);
                                console.log('Formatted result:', formattedDate);
                                return <li key={date}>{formattedDate}</li>;
                            })}
                        </ul>
                    )}
                </div>

                <div>
                    <p className={cn("font-medium", text.primary)}>{t('Time')}</p>
                    <div className="flex items-center gap-2">
                        <p className={text.secondary}>{formatTimeString(bookingDetails.time)}</p>
                        {hasOperationalDelay && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                {t('bookingSlotDelay.badge', { minutes: operationalDelayMinutes })}
                            </span>
                        )}
                    </div>
                </div>

                {/* ETA Information Display */}
                {etaInfo && (etaInfo.baseline?.available || etaInfo.available) && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        {hasOperationalDelay && (
                            <div className="mb-3 flex items-start rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                                <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                                <p>
                                    {t('bookingSlotDelay.etaExclusionNotice', {
                                        minutes: operationalDelayMinutes,
                                    })}
                                </p>
                            </div>
                        )}
                        <div className="flex items-start mb-2">
                            <MapPin className="w-4 h-4 mr-2 mt-1 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                            <div className="space-y-2 flex-1">
                                <h4 className="font-medium text-sm text-blue-900 dark:text-blue-200">
                                    {t('Estimated Schedule')}:
                                </h4>

                                {/* Departure from start location */}
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                    <span className="font-medium">{t('Departure from')} {etaInfo.startLocation.name}:</span>
                                    <span className="ml-2">{formatTime(etaInfo.departureTime)}</span>
                                </div>

                                {/* Arrival at pickup (if different from start) */}
                                {!etaInfo.summary?.isPickupStart && (
                                    <div className="text-sm text-gray-700 dark:text-gray-300">
                                        <span className="font-medium">{t('Arrival at')} {etaInfo.pickupLocation?.name} ({t('pickup')}):</span>
                                        <div className="ml-2 space-y-1">
                                            {/* ORS baseline ETA */}
                                            {etaInfo.baseline?.available && (
                                                <div>
                                                    <span>{formatTime(etaInfo.baseline.pickupLocation.time)}</span>
                                                    <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                        ({formatDurationText(etaInfo.baseline.summary.startToPickupMinutes, t)} - {t('No traffic (ORS)')})
                                                    </span>
                                                </div>
                                            )}
                                            {/* Effective travel, including complete mixed spans. */}
                                            {etaInfo.available && (
                                                <div>
                                                    <span>{formatTime(etaInfo.pickupLocation.time)}</span>
                                                    <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                        ({formatDurationText(etaInfo.summary.startToPickupMinutes, t)} - {t(etaInfo.reference?.available ? 'Reference route' : 'Estimated Schedule')})
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Arrival at dropoff */}
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                    <span className="font-medium">{t('Arrival at')} {etaInfo.dropoffLocation?.name} ({t('dropoff')}):</span>
                                    <div className="ml-2 space-y-1">
                                        {/* ORS baseline ETA */}
                                        {etaInfo.baseline?.available && (
                                            <div>
                                                <span>{formatTime(etaInfo.baseline.dropoffLocation.time)}</span>
                                                <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                    ({formatDurationText(etaInfo.baseline.summary.totalTripMinutes, t)} {t('total')} - {t('No traffic (ORS)')})
                                                </span>
                                            </div>
                                        )}
                                        {/* Effective travel, including complete mixed spans. */}
                                        {etaInfo.available && (
                                            <div>
                                                <span>{formatTime(etaInfo.dropoffLocation.time)}</span>
                                                <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                                    ({formatDurationText(etaInfo.summary.totalTripMinutes, t)} {t('total')} - {t(etaInfo.reference?.available ? 'Reference route' : 'Estimated Schedule')})
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Warning message */}
                        <div className="flex items-start mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                            <AlertTriangle className="w-4 h-4 mr-2 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                {t('Note: Actual times may vary due to road conditions and traffic')}
                            </p>
                        </div>
                    </div>
                )}

                <div>
                    <p className={cn("font-medium", text.primary)}>{t('Passengers')}</p>
                    <p className={text.secondary}>{bookingDetails.passengers}</p>
                </div>

                <div>
                    <p className={cn("font-medium", text.primary)}>{t('Contact Number')}</p>
                    <p className={text.secondary}>{bookingDetails.contactNumber}</p>
                </div>

                {bookingDetails.entityVerificationValue && (
                    <div>
                        <p className={cn("font-medium", text.primary)}>
                            {t('entityVerificationValueLabel')}
                        </p>
                        <p className={text.secondary}>{bookingDetails.entityVerificationValue}</p>
                    </div>
                )}

                {bookingDetails.specialRequests && (
                    <div>
                        <p className={cn("font-medium", text.primary)}>{t('Special Requests')}</p>
                        <p className={text.secondary}>{bookingDetails.specialRequests}</p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default BookingConfirmDialog;
